import { classifyFoundryError } from "@m8t-stack/foundry-invoke";
import { LocalCliError } from "./errors.js";

export interface ProbeResult { ok: boolean; status?: number; message?: string }

/**
 * Probe the Foundry data plane until it serves the project on `consecutive` successive
 * tries (one lucky 200 isn't "ready" on an eventually-consistent fresh account), or give
 * up after `attempts`. A retryable probe failure (transient 404 "Project not found", 5xx,
 * rate-limit) resets the streak and keeps going; a NON-retryable failure (auth) throws.
 *
 * `retryableStatuses` forces the listed HTTP statuses to be treated as retryable regardless
 * of the classifier verdict. Used by the per-agent gate so a registration-propagation 404
 * ("Agent not found") keeps polling instead of failing fast.
 */
export async function awaitDataPlaneReady(opts: {
  probe: () => Promise<ProbeResult>;
  consecutive?: number;
  attempts?: number;
  intervalMs?: number;
  sleep?: (ms: number) => Promise<void>;
  onProgress?: (m: string) => void;
  retryableStatuses?: number[];
  /**
   * A WALL-CLOCK ceiling in ms — the thing a caller can honestly publish.
   *
   * `attempts` and `intervalMs` are a CADENCE, not a bound. Multiplying them
   * produced a number that omitted whatever each attempt itself cost, which is
   * how a budget naming 60 attempts at 5s was measured taking 34 minutes. Worse,
   * the arithmetic that would have been correct has to know every per-attempt
   * term — the token subprocess AND the request — and a term forgotten there is
   * invisible. A deadline needs to know none of them.
   *
   * `attemptCeilingMs` is what one attempt can cost at worst. TWO CONTROLS
   * enforce the deadline, and both are needed:
   *
   *   - a CHECK before an attempt: refuse one that could not finish inside the
   *     budget;
   *   - a CLAMP on the interval sleep: capped at the time left after reserving
   *     the ceiling, so it can never carry the clock past the deadline.
   *
   * THE SLEEP IS CLAMPED, NOT SKIPPED, and the difference is a burst. Sleeping the
   * full interval overruns by `intervalMs - attemptCeilingMs` when the interval is
   * the larger — measured at 10s over a 100s budget with a 30s interval and a 10s
   * ceiling. But SKIPPING the sleep whenever the post-sleep clock would refuse the
   * next attempt is not the fix: an attempt is admitted while `t + C <= B`, so for
   * every `t` in that last window the loop would probe with no pacing at all —
   * measured at 31 probes to a clamp's 4 with a 1s probe cost, and at 151 with a
   * 200ms one — the magnitude scales with per-probe cost, so quoting it
   * without that term repeats the omission that produced the original defect. Clamping keeps both properties: the deadline holds, and the
   * cadence the caller asked for is honoured until the budget genuinely runs out.
   *
   * The guarantee is: total ≤ budgetMs, PROVIDED one attempt really costs at most
   * `attemptCeilingMs`. That proviso is the only one. An under-stated ceiling
   * overruns by the shortfall, which is why the caller derives it from the
   * timeouts it actually imposes.
   */
  budgetMs?: number;
  attemptCeilingMs?: number;
  /** Injectable clock — a deadline whose test has to sleep is a deadline with no test. */
  now?: () => number;
}): Promise<{ ready: boolean; attempts: number; timedOut: boolean }> {
  const consecutive = opts.consecutive ?? 3;
  const attempts = opts.attempts ?? 60;
  const intervalMs = opts.intervalMs ?? 5_000;
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const now = opts.now ?? ((): number => Date.now());
  const startedAt = now();
  const attemptCeilingMs = opts.attemptCeilingMs ?? 0;
  /** True once an attempt starting now could not finish inside the budget. */
  const outOfTime = (): boolean =>
    opts.budgetMs !== undefined && now() - startedAt + attemptCeilingMs > opts.budgetMs;
  /** The interval, capped at the budget left after reserving one attempt. */
  const clampedInterval = (): number => {
    if (opts.budgetMs === undefined) return intervalMs;
    const room = opts.budgetMs - (now() - startedAt) - attemptCeilingMs;
    return Math.max(0, Math.min(intervalMs, room));
  };
  let streak = 0;
  let timedOut = false;
  for (let i = 1; i <= attempts; i++) {
    if (outOfTime()) {
      timedOut = true;
      opts.onProgress?.("data-plane wait reached its budget");
      return { ready: false, attempts: i - 1, timedOut };
    }
    const r = await opts.probe();
    if (r.ok) {
      streak++;
      opts.onProgress?.(`data-plane ok (${streak.toString()}/${consecutive.toString()})`);
      if (streak >= consecutive) return { ready: true, attempts: i, timedOut };
    } else {
      const cls = classifyFoundryError({ status: r.status, message: r.message ?? "" });
      const forcedRetryable = r.status !== undefined && (opts.retryableStatuses?.includes(r.status) ?? false);
      if (!forcedRetryable && !cls.retryable) {
        throw new LocalCliError({
          code: "FOUNDRY_NOT_READY_FATAL",
          message: `Foundry data plane returned a non-retryable error: ${cls.message}`,
          hint: "This is not a transient data-plane delay — check `az login`/permissions and the endpoint.",
          retryable: false,
        });
      }
      streak = 0;
      opts.onProgress?.(`data-plane not ready (${forcedRetryable ? `status ${(r.status ?? 0).toString()}` : cls.category}), waiting…`);
    }
    // Sleep the interval, or whatever is left after reserving one attempt —
    // whichever is smaller. Never longer: that overruns. Zero only at the very
    // end — once the room is gone, at most one more attempt is admitted — and
    // going to zero any earlier is the burst this replaced.
    if (i < attempts) await sleep(clampedInterval());
  }
  return { ready: false, attempts, timedOut };
}
